/* eslint-disable camelcase */
import FileSaver from 'file-saver';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { DateTemplate } from '../../../components/dateTemplate/DateTemplate';
import { EmployeeFilter } from '../../../components/filters/employeeFilter/EmployeeFilter';
import { useGetUserByIDQuery } from '../../../components/scoreCardFilter/scoreCardFilterSlice';
import { ROLES } from '../../../constants';
import {
    useGetAllCountriesQuery,
    useGetCaCategoriesQuery,
    useGetCaRecordsQuery,
    useGetCountiesByEmployeeNumberQuery,
    useGetGlLocationsQuery,
    useGetParentClientsQuery,
    useGetProgramsQuery,
} from '../../../pages/correctiveActions/correctiveActionsSlice';
import {
    selectCurrentEmpid,
    selectCurrentIsAdmin,
    selectCurrentIsHC,
    selectCurrentUserManagerLevel,
} from '../../../pages/login/authSlice';
import { datetimeToDate } from '../../../utils/dateFormat';
import { HCRecordsCA } from '../../../utils/typeConstants';
import { useGetMisConductQuery, useGetSubCategoriesQuery } from '../managerTlViewSearch/CaMangerTLView.slice';
import { CaHcRecord } from '../type';
import './RecordsDashboard.scss';

type RecordsProps = {
    setCaHcRecord: (e: CaHcRecord) => void;
    setActiveTabIndex: (e: number) => void;
};
// eslint-disable-next-line
export const RecordsDashboard = ({ setCaHcRecord, setActiveTabIndex }: RecordsProps) => {
    const isAdmin = useSelector(selectCurrentIsAdmin);
    const isHC = useSelector(selectCurrentIsHC);
    const userManagerLevel = useSelector(selectCurrentUserManagerLevel);
    const [selectedCategory, setSelectedCategory] = useState([]);
    const [categories, setCategories] = useState<any>([]);
    const [caRecords, setCaRecords] = useState<any>([]);
    const [creationStartDate, setCreationStartDate] = useState<Date>(new Date());
    const [creationEndDate, setCreationEndDate] = useState<Date>(new Date());
    const [selectedCountry, setSelectedCountry] = useState(0);
    const [subCategories, setSubcategories] = useState<any>();
    const [selectedSubCategory, setSelectedSubCategory] = useState([]);
    const [selectedSite, setSelectedSite] = useState<any>([]);
    const [selectedParentClient, setSelectedParentClient] = useState([]);
    const [selectedProgram, setSelectedProgram] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filters, setFilters] = useState<any>();
    const [distCategories, setDistCategories] = useState<any>([]);
    const [distSubCategories, setDistSubCategories] = useState<any>([]);
    const [distStatusses, setDistStatusses] = useState<any>([]);
    const [distSeverityLevels, setDistSeverityLevels] = useState<any>([]);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const [allCountries, setAllCountries] = useState<any>([]);
    const [empNumber, setEmpNumber] = useState('');
    const [countryName, setCountryName] = useState('');
    const [siteName, setSiteName] = useState('');
    const [filteredData, setFilteredData] = useState(caRecords);
    const [misconduct, setMisconduct] = useState<any>();
    const [selectedMisconduct, setSelectedMisconduct] = useState([]);
    const [distMisconducts, setDistMisconducts] = useState<any>([]);

    const onValueChange = (e: any) => {
        setFilteredData(e); // Update the filtered data state with the current table data
    };

    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;
        const afilters = { ...filters };
        // @ts-ignore
        afilters.global.value = value;
        setFilters(afilters);
        setGlobalFilterValue(value);
    };
    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            employee_name: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
            },
            scorecard_role_name: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
            },
            author_employee_name: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
            },
            ca_category_name: { value: null, matchMode: FilterMatchMode.IN },
            ca_subcategory_name: { value: null, matchMode: FilterMatchMode.IN },
            ca_misconduct_name: { value: null, matchMode: FilterMatchMode.IN },
            create_date: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
            },
            release_date: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
            },
            expired_date: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
            },
            severity_level_name: { value: null, matchMode: FilterMatchMode.IN },
            ca_status_name: { value: null, matchMode: FilterMatchMode.IN },
        });
        setGlobalFilterValue('');
    };
    const clearFilter = () => {
        initFilters();
    };
    useEffect(() => {
        initFilters();
    }, []);

    const { data: allCountriesData } = useGetAllCountriesQuery();
    const { data: allCategoriesData } = useGetCaCategoriesQuery();
    const { data: allSubCategoriesData } = useGetSubCategoriesQuery(
        {
            categoryId: selectedCategory,
        },
        {
            skip: !selectedCategory,
        }
    );
    const { data: glLocations } = useGetGlLocationsQuery(
        {
            countryId: selectedCountry,
        },
        {
            skip: !selectedCountry,
        }
    );

    const { data: parentClientData } = useGetParentClientsQuery();

    const { data: programData } = useGetProgramsQuery(
        {
            parentClientId: selectedParentClient,
        },
        {
            skip: selectedParentClient?.length === 0,
        }
    );

    const { data: caRecordsData } = useGetCaRecordsQuery(
        {
            employeeNumber: empNumber,
            countryId: selectedCountry,
            CaCategoryIds: selectedCategory,
            SubCategoryIds: selectedSubCategory,
            SiteIds: selectedSite,
            parentClientIds: selectedParentClient,
            programIds: selectedProgram,
            CreationStartDate: datetimeToDate(creationStartDate),
            CreationEndDate: datetimeToDate(creationEndDate),
            MisConductIds: selectedMisconduct,
        },
        { skip: !selectedCountry || !creationStartDate || !creationEndDate || searchText === '' }
    );

    const { data: countriesData } = useGetCountiesByEmployeeNumberQuery(
        { employeeNumber: loggedEmpNum },
        { skip: !loggedEmpNum || !ROLES.Manager.includes(userManagerLevel) }
    );

    const { data: employeeData } = useGetUserByIDQuery(
        {
            filterParam: empNumber,
            searcherEmpNo: loggedEmpNum,
            getAllEmps: (isAdmin || isHC || ROLES.Manager.includes(userManagerLevel)) && true,
        },
        { skip: !empNumber || !loggedEmpNum }
    );

    const { data: misconductData } = useGetMisConductQuery(
        {
            categoryId: selectedCategory.length > 0 ? selectedCategory : null,
            countryId: selectedCountry,
        },
        {
            skip: !selectedCountry,
        }
    );

    useEffect(() => {
        if (employeeData?.data?.length > 0) {
            setSelectedCountry(employeeData?.data[0]?.country_id);
            setCountryName(employeeData?.data[0]?.country_name);
            setSiteName(employeeData?.data[0]?.gl_location_name);
            setSelectedSite([employeeData?.data[0]?.gl_location_id]);
        } else {
            setCountryName('');
            setSiteName('');
        }
    }, [employeeData]);

    useEffect(() => {
        if (allCountriesData?.data?.length > 0) {
            if (isAdmin || isHC) {
                setAllCountries(allCountriesData?.data);
            } else if (countriesData?.data?.length > 0 && ROLES.Manager.includes(userManagerLevel)) {
                setAllCountries(
                    allCountriesData?.data?.filter(
                        (item: any) => item.country_id === countriesData?.data?.[0].country_id
                    )
                );
                setSelectedCountry(countriesData.data[0].country_id);
            }
        }
    }, [countriesData, allCountriesData, selectedCountry, isAdmin, isHC, userManagerLevel]);
    useEffect(() => {
        if (caRecordsData?.data?.length > 0) {
            const data: any = caRecordsData?.data.map((element: any) => {
                return {
                    ...element,
                    create_date: new Date(element.create_date),
                    release_date: element.release_date ? new Date(element.release_date) : null,
                    expired_date: element.expired_date ? new Date(element.expired_date) : null,
                };
            });
            setCaRecords(data);
            const distCategories = Array.from(
                new Set(caRecordsData?.data.map((item: any) => item.ca_category_name))
            );
            setDistCategories(distCategories);
            const distSubCategories = Array.from(
                new Set(caRecordsData?.data.map((item: any) => item.ca_subcategory_name))
            );
            setDistSubCategories(distSubCategories);
            const distMisconducts = Array.from(
                new Set(caRecordsData?.data.map((item: any) => item.ca_misconduct_name))
            );
            setDistMisconducts(distMisconducts);
            const distStatus = Array.from(
                new Set(caRecordsData?.data.map((item: any) => item.ca_status_name))
            );
            setDistStatusses(distStatus);
            const distSeverityLevel = Array.from(
                new Set(caRecordsData?.data.map((item: any) => item.severity_level_name))
            );
            setDistSeverityLevels(distSeverityLevel);
            setSearchText('');
        } else {
            setCaRecords([]);
            setDistCategories([]);
            setDistSubCategories([]);
            setDistMisconducts([]);
            setDistSeverityLevels([]);
            setDistStatusses([]);
            setSearchText('');
        }
    }, [caRecordsData]);
    useEffect(() => {
        let vallSubCategories = [];
        if (allSubCategoriesData?.data?.length > 0) {
            vallSubCategories = allSubCategoriesData.data;
        }
        setSubcategories(vallSubCategories);
    }, [allSubCategoriesData]);
    useEffect(() => {
        const recCountry =
            localStorage.getItem('recCountry') !== null
                ? JSON.parse(localStorage.getItem('recCountry') || '0')
                : 0;
        const recCategory =
            localStorage.getItem('recCategory') !== null
                ? JSON.parse(localStorage.getItem('recCategory') || '0')
                : 0;
        const recSubCategory =
            localStorage.getItem('recSubCategory') !== null
                ? JSON.parse(localStorage.getItem('recSubCategory') || '0')
                : 0;
        const recMisconduct =
            localStorage.getItem('recMisconduct') !== null
                ? JSON.parse(localStorage.getItem('recMisconduct') || '0')
                : 0;
        const recSite =
            localStorage.getItem('recSite') !== null ? JSON.parse(localStorage.getItem('recSite') || '0') : 0;
        const recCreationStartDate =
            localStorage.getItem('recCreationStartDate') !== null
                ? JSON.parse(localStorage.getItem('recCreationStartDate') || '')
                : new Date();
        const recCreationEndDate =
            localStorage.getItem('recCreationEndDate') !== null
                ? JSON.parse(localStorage.getItem('recCreationEndDate') || '')
                : new Date();
        let recEmpNo =
            localStorage.getItem('recEmpNo') !== null
                ? JSON.parse(localStorage.getItem('recEmpNo') || '0')
                : '';
        if (recEmpNo === '0' || recEmpNo === null || recEmpNo === 0) {
            recEmpNo = '';
        }
        if (recCountry !== 0) {
            setSearchText('search');
            setSelectedCategory(recCategory);
            setSelectedCountry(recCountry);
            setSelectedSubCategory(recSubCategory);
            setSelectedMisconduct(recMisconduct);
            setSelectedSite(recSite);
            setCreationStartDate(new Date(recCreationStartDate));
            setCreationEndDate(new Date(recCreationEndDate));
            setEmpNumber(recEmpNo);
        } else {
            setCreationStartDate(new Date());
            setCreationEndDate(new Date());
        }
    }, []);

    useEffect(() => {
        if (empNumber !== '') {
            setSearchText('search');
        }
    }, [empNumber]);

    useEffect(() => {
        setCategories(allCategoriesData?.data?.length > 0 ? allCategoriesData.data : []);
    }, [allCategoriesData]);

    useEffect(() => {
        if (misconductData?.data?.length > 0) {
            const misconductList = misconductData.data.map((item: any) => ({
                ca_misconduct_id: item.ca_misconduct_id,
                ca_misconduct_name: item.ca_misconduct_name,
            }));
            setMisconduct(misconductList);
        } else {
            setMisconduct([]);
        }
    }, [misconductData]);

    const ClearLocalStorage = () => {
        window.localStorage.removeItem('recCountry');
        window.localStorage.removeItem('recCategory');
        window.localStorage.removeItem('recSubCategory');
        window.localStorage.removeItem('recMisconduct');
        window.localStorage.removeItem('recSite');
        window.localStorage.removeItem('recCreationStartDate');
        window.localStorage.removeItem('recCreationEndDate');
        window.localStorage.removeItem('recEmp');
    };
    const changeCreationStartDate = (e: any) => {
        setCreationStartDate(e.value);
        window.localStorage.recCreationStartDate = JSON.stringify(e.value);
    };

    const changecreationEndDate = (e: any) => {
        setCreationEndDate(e.value);
        window.localStorage.recCreationEndDate = JSON.stringify(e.value);
    };
    const exportExcel = () => {
        const data = filteredData?.length > 0 ? filteredData : caRecords;

        if (!data || data.length === 0) {
            toast.info('No data found.');
            return;
        }

        import('xlsx')
            .then((xlsx: any) => {
                // Format data for export - convert dates to strings
                const formattedData = data.map((record: any) => ({
                    ...record,
                    create_date: record.create_date
                        ? new Date(record.create_date).toLocaleDateString()
                        : '',
                    release_date: record.release_date
                        ? new Date(record.release_date).toLocaleDateString()
                        : '',
                    expired_date: record.expired_date
                        ? new Date(record.expired_date).toLocaleDateString()
                        : '',
                }));

                const worksheet = xlsx.utils.json_to_sheet(formattedData);
                const workbook = {
                    Sheets: { data: worksheet },
                    SheetNames: ['data']
                };
                const excelBuffer = xlsx.write(workbook, {
                    bookType: 'xlsx',
                    type: 'array'
                });
                saveAsExcelFile(excelBuffer, 'ca_records');
            })
            .catch((error) => {
                console.error('Error exporting to Excel:', error);
                toast.error('Failed to export data to Excel.');
            });
    };
    const saveAsExcelFile = (buffer: any, fileName: any) => {
        const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const EXCEL_EXTENSION = '.xlsx';
        const data = new Blob([buffer], {
            type: EXCEL_TYPE,
        });
        FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
    };
    const renderHeader = () => {
        return (
            <div className="exportexcel">
                <Divider />
                <Button type="button" icon="pi pi-filter-slash" className='btn-primary' label="Clear" onClick={clearFilter} />
                <span className="p-input-icon-left ms-3">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilterValue}
                        onChange={onGlobalFilterChange}
                        placeholder="Keyword Search"
                    />
                </span>
                <Button
                    type="button"
                    icon="pi pi-upload me-2"
                    className="btn-export mt-2 me-2 float-end"
                    onClick={() => exportExcel()}
                    data-pr-tooltip="XLS"
                >
                    Export to Excel
                </Button>
            </div>
        );
    };
    const handleViewClick = (rowData: CaHcRecord) => {
        const hcRecord: CaHcRecord = rowData;
        setCaHcRecord(hcRecord);
        setActiveTabIndex(HCRecordsCA.RecordView);
        window.localStorage.recCountry = selectedCountry;
        window.localStorage.recCategory = JSON.stringify(selectedCategory);
        window.localStorage.recSubCategory = JSON.stringify(selectedSubCategory);
        window.localStorage.recMisconduct = JSON.stringify(selectedMisconduct);
        //window.localStorage.recCreationStartDate = JSON.stringify(creationStartDate);
        //window.localStorage.recCreationEndDate = JSON.stringify(creationEndDate);
        window.localStorage.recSite = JSON.stringify(selectedSite);
        window.localStorage.recEmpNo = empNumber;
    };
    const viewTemplate = (rowData: any) => {
        return (
            <div style={{ textAlign: 'center' }}>
                <Button
                    type="button"
                    onClick={() => {
                        handleViewClick(rowData);
                    }}
                    icon="pi pi-eye"
                    style={{ background: 'transparent', border: '#CECECD 1px solid', color: '#3F5765' }}
                />
            </div>
        );
    };
    const dateOnchanges = (e: any, options: any) => {
        options.filterCallback(e.value, options.index);
    };
    const dateFilterTemplate = (options: any) => {
        return (
            <Calendar
                value={options.value}
                onChange={(e: any) => dateOnchanges(e, options)}
                dateFormat="mm/dd/yy"
                placeholder="dd/mm/yy"
                mask="99/99/9999"
                className="custom-calendar"
            />
        );
    };
    const CategoryfilterOnChange = (options: any, e: any) => {
        options.filterCallback(e.value);
    };
    const categoryFilterTemplate = (options: any) => {
        return (
            <MultiSelect
                value={options.value}
                options={distCategories}
                onChange={(e: any) => {
                    CategoryfilterOnChange(options, e);
                }}
                placeholder="Any"
                className="p-column-filter"
            />
        );
    };
    const SeveritylevelfilterOnChange = (options: any, e: any) => {
        options.filterCallback(e.value);
    };
    const severityLevelFilterTemplate = (options: any) => {
        return (
            <MultiSelect
                value={options.value}
                options={distSeverityLevels}
                onChange={(e: any) => {
                    SeveritylevelfilterOnChange(options, e);
                }}
                placeholder="Any"
                className="p-column-filter"
            />
        );
    };
    const StatusfilterOnChange = (options: any, e: any) => {
        options.filterCallback(e.value);
    };
    const statusFilterTemplate = (options: any) => {
        return (
            <MultiSelect
                value={options.value}
                options={distStatusses}
                onChange={(e: any) => {
                    StatusfilterOnChange(options, e);
                }}
                placeholder="Any"
                className="p-column-filter"
            />
        );
    };
    const SubCategoryfilterOnChange = (options: any, e: any) => {
        options.filterCallback(e.value);
    };
    const subCategoryFilterTemplate = (options: any) => {
        return (
            <MultiSelect
                value={options.value}
                options={distSubCategories}
                onChange={(e: any) => {
                    SubCategoryfilterOnChange(options, e);
                }}
                placeholder="Any"
                className="p-column-filter"
            />
        );
    };
    const MisconductfilterOnChange = (options: any, e: any) => {
        options.filterCallback(e.value);
    };
    const misconductFilterTemplate = (options: any) => {
        return (
            <MultiSelect
                value={options.value}
                options={distMisconducts}
                onChange={(e: any) => {
                    MisconductfilterOnChange(options, e);
                }}
                placeholder="Any"
                className="p-column-filter"
            />
        );
    };
    const header = renderHeader();
    const onSearchClick = () => {
        if (selectedCountry === 0 || selectedCountry === null) toast.warning('Please select Country');
        if (creationStartDate === null) toast.warning('Please select Creation Start Date');
        if (creationEndDate === null) toast.warning('Please select Creation End Date');
        if (creationStartDate > creationEndDate)
            toast.warning('Creation End Date should be greater than or equal to Start Date.');
        else setSearchText('search');
    };
    const resetAllFilters = () => {
        setSelectedCountry(0);
        setSelectedSite([]);
        setSelectedCategory([]);
        setSelectedSubCategory([]);
        setSelectedMisconduct([]);
        setSelectedParentClient([]);
        setSelectedProgram([]);
        setCreationStartDate(new Date());
        setCreationEndDate(new Date());
        setCaRecords([]);
        setGlobalFilterValue('');
        initFilters();
        ClearLocalStorage();
        setCountryName('');
        setSiteName('');
        setEmpNumber('');
    };

    return (
        <>
            <div className="row mt-2">
                {(isAdmin || isHC) && (
                    <div className="row">
                        <EmployeeFilter resetDate={resetAllFilters} setAgentNumber={setEmpNumber} />
                    </div>
                )}
            </div>
            <div className="row mt-2 g-3">

                <div className="col-md-2">
                    <div className="content">
                        <label htmlFor="country">
                            Country<span className="text-danger">*</span>
                        </label>
                        {countryName?.length > 0 && (
                            <InputText disabled placeholder="Country" value={countryName} />
                        )}

                        {!(isAdmin || isHC) && ROLES.Manager.includes(userManagerLevel) && (
                            <InputText disabled placeholder="Country" value={allCountries[0]?.country_name} />
                        )}

                        {(isAdmin || isHC) && countryName?.length <= 0 && (
                            <Dropdown
                                id="country"
                                filter
                                placeholder="Select Country"
                                // className="rounded-start"
                                className={`custom-dropdown w-200 ${selectedCountry ? '' : 'p-invalid'}`}
                                options={allCountries}
                                optionValue={'country_id'}
                                optionLabel={'country_name'}
                                value={selectedCountry}
                                onChange={(e) => {
                                    setSelectedCountry(e.target.value);
                                    setSelectedSite([]);
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="col-md-2">
                    <div className="content">
                        <label htmlFor="recipientName">Category</label>
                        <MultiSelect
                            id="recipientName"
                            filter
                            placeholder="Select Category"
                            options={categories}
                            optionValue={'ca_category_id'}
                            optionLabel={'ca_category_name'}
                            value={selectedCategory}
                            onChange={(e) => {
                                setSelectedCategory(e.target.value);
                                setSelectedSubCategory([]);
                                setSelectedMisconduct([]);
                            }}
                            maxSelectedLabels={3}
                            className="custom-dropdown w-200"
                        />
                    </div>
                </div>
                {misconductData?.data?.length > 0 ? (
                    <div className="col-md-2">
                        <div className="content">
                            <label htmlFor="countryId">Misconduct</label>
                            <MultiSelect
                                id="Misconduct"
                                filter
                                placeholder="Select Misconduct"
                                className="custom-dropdown w-200"
                                options={misconduct}
                                optionValue={'ca_misconduct_id'}
                                optionLabel={'ca_misconduct_name'}
                                value={selectedMisconduct}
                                onChange={(e: any) => {
                                    setSelectedMisconduct(e.target.value);
                                }}
                                maxSelectedLabels={3}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="col-md-2">
                        <div className="content">
                            <label htmlFor="subCategory">Sub Category</label>
                            <MultiSelect
                                id="subCategory"
                                filter
                                placeholder="Select Sub Category"
                                className="custom-dropdown w-200"
                                options={subCategories}
                                optionValue={'ca_subcategory_id'}
                                optionLabel={'ca_subcategory_name'}
                                value={selectedSubCategory}
                                onChange={(e: any) => {
                                    setSelectedSubCategory(e.target.value);
                                }}
                                maxSelectedLabels={3}
                            />
                        </div>
                    </div>
                )}
                <div className="col-md-3">
                    <div className="content">
                        <label htmlFor="recipientName">Creation Start Date</label>
                        <Calendar
                            id="icon"
                            value={creationStartDate}
                            dateFormat="dd-M-yy"
                            onChange={changeCreationStartDate}
                            showIcon
                            className="custom-calendar"
                        />
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="content">
                        <label htmlFor="recipientName">Creation End Date</label>
                        <Calendar
                            id="icon"
                            value={creationEndDate}
                            dateFormat="dd-M-yy"
                            onChange={changecreationEndDate}
                            showIcon
                            className="custom-calendar"
                        />
                    </div>
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-md-4">
                    <div className="content">
                        <label htmlFor="countrydd">Location</label>
                        {siteName?.length > 0 && <InputText disabled placeholder="site" value={siteName} />}

                        {siteName?.length <= 0 && (
                            <MultiSelect
                                id="site"
                                filter
                                placeholder="Select Site"
                                className="custom-dropdown"
                                options={glLocations?.data}
                                optionValue={'gl_location_id'}
                                optionLabel={'gl_location_name'}
                                value={selectedSite}
                                onChange={(e: any) => {
                                    setSelectedSite(e.target.value);
                                }}
                                showSelectAll={false}
                                maxSelectedLabels={1}
                                selectionLimit={20}
                                showClear
                            />
                        )}
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="content">
                        <label htmlFor="ParentClient">Parent Client</label>
                        <MultiSelect
                            id="ParentClient"
                            filter
                            placeholder="Select Parent Client"
                            className="custom-dropdown"
                            options={parentClientData?.data}
                            optionValue={'parent_client_id'}
                            optionLabel={'parent_client_name'}
                            value={selectedParentClient}
                            onChange={(e: any) => {
                                setSelectedProgram([]);
                                setSelectedParentClient(e.target.value);
                            }}
                            showSelectAll={false}
                            maxSelectedLabels={1}
                            selectionLimit={20}
                            showClear
                        />
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="content">
                        <label htmlFor="Program">Program</label>
                        <MultiSelect
                            id="Program"
                            filter
                            placeholder="Select Program"
                            className="custom-dropdown"
                            options={programData?.data}
                            optionValue={'program_id'}
                            optionLabel={'program_name'}
                            value={selectedProgram}
                            onChange={(e: any) => {
                                setSelectedProgram(e.target.value);
                            }}
                            showSelectAll={false}
                            maxSelectedLabels={1}
                            selectionLimit={20}
                            showClear
                        />
                    </div>
                </div>
                <div className="col-md-12 mt-4">
                    <Button
                        label="Search"
                        style={{
                            fontWeight: 400,
                        }}
                        className="btn-primary mb-2 float-end"
                        onClick={onSearchClick}
                    />
                    {!(isAdmin || isHC) && (
                        <Button
                            label="Reset Filters"
                            style={{
                                fontWeight: 400,
                            }}
                            className="btn-secondary mb-2 me-2 float-end"
                            onClick={resetAllFilters}
                        />
                    )}
                </div>
            </div>

            <div className="row mt-4">
                <div className="col-md-12">
                    <DataTable
                        header={header}
                        value={caRecords}
                        onValueChange={onValueChange}
                        className="carecord-dashboard"
                        dataKey="corrective_action_id"
                        rows={5}
                        paginator={caRecords?.length > 5}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                        emptyMessage="No employees found."
                        filters={filters}
                        scrollable
                        scrollDirection="horizontal"
                        globalFilterFields={[
                            'employee_name',
                            'scorecard_role_name',
                            'create_date',
                            'author_employee_name',
                            'ca_category_name',
                            'ca_subcategory_name',
                            'ca_misconduct_name',
                            'severity_level_name',
                            'ca_status_name',
                        ]}
                    >
                        <Column
                            header="Agent"
                            field="employee_name"
                            sortable
                            sortField="employee_name"
                            filter
                            filterPlaceholder="Search by Agent"
                            style={{ minWidth: '10rem' }}
                        />
                        <Column
                            header="Role"
                            field="scorecard_role_name"
                            sortable
                            sortField="scorecard_role_name"
                            filter
                            filterPlaceholder="Search by Role"
                            style={{ minWidth: '10rem' }}
                        />
                        <Column
                            header="Creation-Date"
                            field="create_date"
                            sortable
                            sortField="create_date"
                            filterField="create_date"
                            body={(date) => DateTemplate(date?.create_date)}
                            filter
                            dataType="date"
                            filterElement={dateFilterTemplate}
                            style={{ minWidth: '10rem' }}
                        />
                        <Column
                            header="Author"
                            field="author_employee_name"
                            sortable
                            sortField="author_employee_name"
                            filter
                            filterPlaceholder="Search by Author"
                            style={{ minWidth: '10rem' }}
                        />
                        <Column
                            header="Category"
                            field="ca_category_name"
                            sortable
                            sortField="ca_category_name"
                            showFilterMatchModes={false}
                            filter
                            filterElement={categoryFilterTemplate}
                            style={{ minWidth: '10rem' }}
                        />
                        {misconductData?.data?.length > 0 ? (
                            <Column
                                header="Misconduct"
                                field="ca_misconduct_name"
                                sortable
                                sortField="ca_misconduct_name"
                                filterField="ca_misconduct_name"
                                showFilterMatchModes={false}
                                filter
                                filterElement={misconductFilterTemplate}
                                style={{ minWidth: '10rem' }}
                            />
                        ) : (
                            <Column
                                header="Sub-Category"
                                field="ca_subcategory_name"
                                sortable
                                sortField="ca_subcategory_name"
                                filterField="ca_subcategory_name"
                                showFilterMatchModes={false}
                                filter
                                filterElement={subCategoryFilterTemplate}
                                style={{ minWidth: '10rem' }}
                            />
                        )}

                        <Column
                            header="Severity-Level"
                            field="severity_level_name"
                            sortable
                            sortField="severity_level_name"
                            filterField="severity_level_name"
                            showFilterMatchModes={false}
                            filter
                            filterElement={severityLevelFilterTemplate}
                            style={{ minWidth: '10rem' }}
                        />
                        <Column
                            header="Release"
                            field="release_date"
                            sortable
                            sortField="release_date"
                            body={(date) => (date.release_date ? DateTemplate(date?.release_date) : '--')}
                            filter
                            filterPlaceholder="Search by date"
                            dataType="date"
                            filterElement={dateFilterTemplate}
                            style={{ minWidth: '10rem' }}
                        />
                        <Column
                            header="Expiry"
                            field="expired_date"
                            sortable
                            sortField="expired_date"
                            body={(date) => (date.expired_date ? DateTemplate(date?.expired_date) : '--')}
                            filter
                            filterPlaceholder="Search by date"
                            dataType="date"
                            filterElement={dateFilterTemplate}
                            style={{ minWidth: '10rem' }}
                        />
                        <Column
                            header="Status"
                            field="ca_status_name"
                            sortable
                            sortField="ca_status_name"
                            filterField="ca_status_name"
                            showFilterMatchModes={false}
                            filter
                            filterElement={statusFilterTemplate}
                            style={{ minWidth: '10rem' }}
                        />
                        <Column body={viewTemplate} frozen alignFrozen="right" />
                    </DataTable>
                </div>
            </div>
        </>
    );
};
